import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  Save, 
  Loader2, 
  Globe, 
  FileText, 
  Image, 
  Code,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SEOSettings() {
  const [settings, setSettings] = useState({
    site_title: "CRM Cash",
    site_description: "",
    meta_keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    favicon_url: "",
    header_scripts: "",
    body_scripts: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/seo`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
      toast.error("Ошибка загрузки настроек");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/seo`, settings, {
        headers: getAuthHeaders()
      });
      toast.success("SEO настройки сохранены!");
    } catch (error) {
      console.error("Error saving SEO settings:", error);
      toast.error("Ошибка сохранения настроек");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="seo-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
              <Search className="w-5 h-5 text-white" />
            </div>
            SEO Настройки
          </h1>
          <p className="text-slate-500 mt-1">
            Настройка заголовков, мета-тегов и скриптов
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Обновить
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Сохранить
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic SEO */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5 text-blue-500" />
              Основные настройки
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Заголовок сайта (Title)</Label>
              <Input
                value={settings.site_title}
                onChange={(e) => handleChange("site_title", e.target.value)}
                placeholder="CRM Cash"
              />
              <p className="text-xs text-slate-400 mt-1">
                Отображается во вкладке браузера
              </p>
            </div>
            
            <div>
              <Label>Описание сайта (Description)</Label>
              <Textarea
                value={settings.site_description}
                onChange={(e) => handleChange("site_description", e.target.value)}
                placeholder="CRM система управления профилями"
                rows={3}
              />
              <p className="text-xs text-slate-400 mt-1">
                Описание для поисковых систем (до 160 символов)
              </p>
            </div>
            
            <div>
              <Label>Ключевые слова (Keywords)</Label>
              <Input
                value={settings.meta_keywords}
                onChange={(e) => handleChange("meta_keywords", e.target.value)}
                placeholder="crm, управление, профили"
              />
              <p className="text-xs text-slate-400 mt-1">
                Через запятую
              </p>
            </div>
            
            <div>
              <Label>URL Favicon</Label>
              <Input
                value={settings.favicon_url}
                onChange={(e) => handleChange("favicon_url", e.target.value)}
                placeholder="https://example.com/favicon.ico"
              />
            </div>
          </CardContent>
        </Card>

        {/* Open Graph */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Image className="w-5 h-5 text-purple-500" />
              Open Graph (соц. сети)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>OG Заголовок</Label>
              <Input
                value={settings.og_title}
                onChange={(e) => handleChange("og_title", e.target.value)}
                placeholder="Оставьте пустым для использования основного"
              />
              <p className="text-xs text-slate-400 mt-1">
                Заголовок при шеринге в соц. сетях
              </p>
            </div>
            
            <div>
              <Label>OG Описание</Label>
              <Textarea
                value={settings.og_description}
                onChange={(e) => handleChange("og_description", e.target.value)}
                placeholder="Описание для соц. сетей"
                rows={3}
              />
            </div>
            
            <div>
              <Label>OG Изображение (URL)</Label>
              <Input
                value={settings.og_image}
                onChange={(e) => handleChange("og_image", e.target.value)}
                placeholder="https://example.com/og-image.jpg"
              />
              <p className="text-xs text-slate-400 mt-1">
                Рекомендуемый размер: 1200x630px
              </p>
            </div>
            
            {settings.og_image && (
              <div className="mt-2">
                <Label className="text-xs">Превью:</Label>
                <img 
                  src={settings.og_image} 
                  alt="OG Preview" 
                  className="mt-1 max-h-32 rounded-lg border"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Scripts */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Code className="w-5 h-5 text-orange-500" />
              Пользовательские скрипты
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Скрипты в &lt;head&gt;</Label>
              <Textarea
                value={settings.header_scripts}
                onChange={(e) => handleChange("header_scripts", e.target.value)}
                placeholder="<!-- Google Analytics, Yandex Metrika и т.д. -->"
                rows={4}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-400 mt-1">
                Код будет добавлен перед закрывающим тегом &lt;/head&gt;
              </p>
            </div>
            
            <div>
              <Label>Скрипты в &lt;body&gt;</Label>
              <Textarea
                value={settings.body_scripts}
                onChange={(e) => handleChange("body_scripts", e.target.value)}
                placeholder="<!-- Чат-виджеты, пиксели и т.д. -->"
                rows={4}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-400 mt-1">
                Код будет добавлен перед закрывающим тегом &lt;/body&gt;
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
